import React, {useEffect, useState} from 'react'
import {sum, convertToHumanReadable} from '@/Utilities/Utilities'
import {Container, Dropdown, Subheader, Benchmark} from '@/Components'
import {Calendar, DownloadCloud} from 'react-feather'
import AppLayout from "@/Layouts/AppLayout";
import {Select} from "@/Components/Form";
import DatePicker from "react-datepicker";
import {Inertia} from '@inertiajs/inertia';
import {
  dataEntryFilterHelpers,
  EntriesBarChart,
  EntriesPieChart,
  getTeamOptions,
  prepareEntryData,
  gases
} from "@/Utilities/EntryGraphHelpers";
import {
  endOfMonth,
  isAfter,
  isBefore,
  startOfMonth,
  subDays,
  subYears
} from 'date-fns';

import "react-datepicker/dist/react-datepicker.css";

export default function Dashboard({teams, scopes, benchmark}) {

  const [startDate, setStartDate] = useState(startOfMonth(subYears(new Date(), 1)));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [pieData, setPieData] = useState(null);
  const [filters, setFilters] = useState({
    activity: 0,
    location: "",
    gas: "co2e",
    format: "co2e_ar5"
  });

  const teamOptions = getTeamOptions(teams);
  const currentGasLabel = gases.find(g => g.value === filters.gas)?.label || 'CO₂';
  const setTeamAndPopulateGraphData = prepareEntryData(setCurrentTeamId, teamOptions, endDate, startDate, teams, filters, setGraphData, setPieData);
  const {applyFilter, getFilterOptions} = dataEntryFilterHelpers(filters, setFilters, currentTeamId, teamOptions);

  const scopeData = {
    scope_1: pieData ? Math.round(pieData[1]) : 0,
    scope_2: pieData ? Math.round(pieData[2]) : 0,
    scope_3: pieData ? Math.round(pieData[3]) : 0,
  };

  const [average, setAverage] = useState({
    employees: 0,
    traded: 0,
  });

  useEffect(() => {
    setTeamAndPopulateGraphData(currentTeamId);
  }, [startDate, endDate, filters]);

  useEffect(() => {
    window.globalGasType = currentGasLabel;
    window.globalGraphData = {...graphData};

    if(!benchmark.yearly_revenue || !benchmark.employees || !benchmark.industry) {
      setAverage({
        employees: 0,
        traded: 0,
      });
    } else {
      setAverage({
          employees: (sum(pieData) / benchmark.employees) < 1 ? (sum(pieData) / benchmark.employees).toFixed(1) : Math.round(sum(pieData) / benchmark.employees),
          traded: (sum(pieData) / benchmark.yearly_revenue) < 1 ? (sum(pieData) / benchmark.yearly_revenue).toFixed(1) : Math.round(sum(pieData) / benchmark.yearly_revenue),
      });
    }

  }, [graphData] );


  // Make some distance to the top of the graphs
  const maximumEmployee = () => {
    return Math.max(average.employees, benchmark.industry_average.employeehigh) + benchmark.industry_average.employeelow;
  }
  const maximumTraded = () => {
    return Math.max(average.traded, benchmark.industry_average.tradedhigh) + benchmark.industry_average.tradedlow;
  }

  return (
      <Container size={"4xl:max-w-screen-3xl"} padding={"px-4 lg:px-16 4xl:pl-32"} className="4xl:mx-0 mt-4 sm:dots-orange">
      <div className="p-4">
        <h2 className="text-5xl font-bold">Your emissions</h2>

        <Subheader margin="mb-2 mt-6">STATISTICS</Subheader>
        <p className="mb-4">Build the emission accounting by adding activities and reporters for activities under scope 1 to 3</p>

        <div className="flex mb-4">
          <div className="w-full mb-4 md:w-1/2">
            <Select name="team" defaultOption="All teams" options={teamOptions} onChange={e => { setTeamAndPopulateGraphData(e.target.value) }} className="mb-4 h-14"/>
          </div>
          <div className="w-full mb-4 md:ml-3 md:w-1/2">
            <div className="flex flex-row items-center justify-start px-3 py-1 bg-white border h-14 border-borderColor datepicker-container">
              <Calendar size={18} className="relative z-10 mr-1 pointer-events-none"/>
              <DatePicker
                selected={startDate}
                onChange={date => {
                  if (isAfter(date, endDate)) {
                    date = subDays(endDate, 1);
                  }
                  setStartDate(date)
                }}
                className="px-1 py-1 border-2 border-transparent w-[100px]"
                dateFormat="yyyy-MM-dd"
              />
              <span className="mx-2">&ndash;</span>
              <DatePicker
                selected={endDate}
                onChange={date => {
                  if (isBefore(date, startDate)) {
                    date = endOfMonth(startDate);
                  }
                  setEndDate(date)
                }}
                className="px-1 py-1 border-2 border-transparent w-[100px]"
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center mb-4">
          <Dropdown title="Activity"
                    options={getFilterOptions('activities')}
                    placeholder="All activities" onSelect={value => applyFilter('activity', value)}/>
          <Dropdown title="Locations"
                    options={getFilterOptions('locations')}
                    placeholder="All locations" onSelect={value => applyFilter('location', value)}/>
          <Dropdown title="Green house gases"
                    options={gases}
                    placeholder="CO₂e" onSelect={value => applyFilter('gas', value)}/>
            {filters.gas === 'co2e' ?
            <Dropdown title="IPCC report format"
                      options={[{label: 'AR5', value: 'co2e_ar5'}, {label: 'AR4', value: 'co2e_ar4'}]}
                      placeholder="AR5" onSelect={value => applyFilter('format', value)}/>
            : null}
        </div>

        {sum(pieData) !== 0 && (
          <>
            <div className="flex mb-4">
              <div className="w-full mb-4 md:w-2/5">
                <EntriesPieChart pieData={pieData} graphData={graphData}/>
              </div>
              <div className="flex flex-col justify-center w-full mb-4 text-center md:w-3/5">
                <p className="mb-4">During this period</p>
                <p
                  className="mb-5 text-6xl font-bold">{Intl.NumberFormat('sv-SE').format(Math.round(sum(pieData)))} kg</p>
                <p className="text-lg">{currentGasLabel} was emitted to air</p>
              </div>
            </div>

            <div className="flex flex-col mb-4 bg-white">
              <div className="flex flex-col py-4 bg-white md:flex-row">
                <div className="flex-1 px-4 pb-2 overflow-hidden">
                  <EntriesBarChart graphData={graphData}/>
                </div>
              </div>
            </div>

            {(!benchmark.yearly_revenue || !benchmark.employees || !benchmark.industry) ? (
            <div className="flex flex-col mb-4 bg-white">
              <div className="flex flex-col py-4 bg-white md:flex-row">
                <div className="flex-1 px-4 pb-2 overflow-hidden">
                    <p>Industry benchmark</p>

                    <div className="flex flex-row items-center justify-between p-4 bg-primary-light">
                        <p>Do you want to see your industry benchmark</p>
                        <a href="/company-settings?focus=industry" className="underline hover:nounderline">Complete your settings</a>
                    </div>
                </div>
              </div>
            </div>
            ) : (
            <div className="flex flex-col mb-4 bg-white">
                <div className="flex flex-col bg-white md:flex-row">
                    <div className="flex-1 px-4 pb-2 overflow-hidden">
                        <Subheader margin="mb-2 mt-6">Industry benchmark</Subheader>

                        <div className="flex flex-row flex-wrap">
                            <div className="w-full md:w-1/2">
                                <p>Emission</p>
                                <h3 className="text-2xl font-bold">per employee</h3>
                                <div className="flex flex-row items-stretch justify-start w-full">
                                    <div className="flex flex-col items-center justify-center w-1/2 px-4 py-8 min-w-fit bg-primary-light">
                                        <span className="inline-block text-6xl font-bold">{average.employees}</span>
                                        <span>Kg CO₂</span>
                                    </div>
                                    <Benchmark maxValue={maximumEmployee()} value={average.employees} industryAverageLow={benchmark.industry_average.employeelow} industryAverageHigh={benchmark.industry_average.employeehigh} />
                                </div>
                            </div>

                            <div className="w-full mt-8 md:mt-0 md:w-1/2">
                                <p>Emission</p>
                                <h3 className="text-2xl font-bold">per TKR traded</h3>
                                <div className="flex flex-row items-stretch justify-start w-full">
                                    <div className="flex flex-col items-center justify-center w-1/2 px-4 py-8 min-w-fit bg-apricot-light">
                                        <span className="inline-block text-6xl font-bold">{average.traded}</span>
                                        <span>Kg CO₂</span>
                                    </div>
                                    <Benchmark maxValue={maximumTraded()} value={average.traded} industryAverageLow={benchmark.industry_average.tradedlow} industryAverageHigh={benchmark.industry_average.tradedhigh} />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-row items-center justify-between p-4 mt-16 mb-4 bg-gray-lighter">
                            <p>Based on <strong>{benchmark.employees} employees</strong> and <strong>{Intl.NumberFormat('sv-SE').format(benchmark.yearly_revenue)} TKR</strong> yearly revenue</p>
                            <a href="/company-settings?focus=industry" className="underline hover:nounderline text-blue">Change your settings</a>
                        </div>
                    </div>
                </div>
            </div>
            )}


            <div className="self-end flex-grow my-8 text-right place-self-end">
              <a download href={`/dashboard/export?${new URLSearchParams(scopeData).toString()}`} className="underline text-blue">
                <DownloadCloud className="inline mr-2"/>
                Download report
              </a>
            </div>

          </>
        )}

        {sum(pieData) === 0 && (
          <div className="p-4 mt-6 bg-primary-light">Report some data in order to display statistics</div>
        )}

        {!pieData && (
          <div className="p-4 bg-primary-light">Select a team to begin exploring your emissions</div>
        )}

      </div>
    </Container>
  )
}


Dashboard.layout = page => (
  <AppLayout title="Your emissions">{page}</AppLayout>
)
